import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LocationProvider } from './contexts/LocationContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { NotificationToastContainer } from './roles/shared/components/Common/NotificationToast';
import { requestNotificationPermission } from './utils/notificationUtils';
import './App.css';

// Client pages
import HomePage from './roles/client/pages/HomePage';
import LoginPage from './roles/client/pages/LoginPage';
import SignUpPage from './roles/client/pages/SignUpPage';
import DashboardPage from './roles/client/pages/DashboardPage';
import ForgotPasswordPage from './roles/client/pages/ForgotPasswordPage';
import ResetPasswordPage from './roles/client/pages/ResetPasswordPage';
import GetStartedPage from './roles/client/pages/GetStartedPage';
import NotificationsPage from './roles/client/pages/NotificationsPage';
import OrdersPage from './roles/client/pages/OrdersPage';
import OrdersDetailPage from './roles/client/pages/OrdersDetailPage';
import OrderTrackingPage from './roles/client/pages/OrderTrackingPage';
import OrderReportIssuePage from './roles/client/pages/OrderReportIssuePage';
import OrderReviewPage from './roles/client/pages/OrderReviewPage';
import ReferralsPage from './roles/client/pages/ReferralsPage';
import PaymentPage from './roles/client/pages/PaymentPage';
import PaymentCallbackPage from './roles/client/pages/PaymentCallbackPage';
import HandymanFinalPaymentPage from './roles/client/pages/HandymanFinalPaymentPage';

// Client components
import ClientDashboard from './roles/client/components/ClientDashboard';
import CreateOrder from './roles/client/components/CreateOrder';
import Shop from './roles/client/components/Services/Shop';
import PickupDelivery from './roles/client/components/Services/PickupDelivery';
import CargoDelivery from './roles/client/components/Services/CargoDelivery';

// Shared pages
import AboutPage from './roles/shared/pages/AboutPage';
import ContactPage from './roles/shared/pages/ContactPage';
import FeaturesPage from './roles/shared/pages/FeaturesPage';
import PrivacyPolicyPage from './roles/shared/pages/PrivacyPolicyPage';
import TermsOfServicePage from './roles/shared/pages/TermsOfServicePage';
import AccountDeletionPage from './roles/shared/pages/AccountDeletionPage';

// Shared components
import AuthCallback from './roles/shared/components/Auth/AuthCallback';
import OTPVerification from './roles/shared/components/Auth/OTPVerification';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
};

function App() {
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  return (
    <Router>
      <AuthProvider>
        <NotificationProvider>
          <LocationProvider>
            <WebSocketProvider>
              <NotificationToastContainer />
              <Routes>
                {/* Public */}
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignUpPage />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/get-started" element={<GetStartedPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                <Route path="/how-to-delete-account" element={<AccountDeletionPage />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/verify-email/:token" element={<AuthCallback />} />
                <Route path="/verify-phone" element={<OTPVerification />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/payment/callback" element={<PaymentCallbackPage />} />

                {/* Client protected routes */}
                <Route path="/dashboard" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
                <Route path="/client/dashboard" element={<ProtectedRoute><ClientDashboard /></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
                <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
                <Route path="/my-orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
                <Route path="/orders/:id" element={<ProtectedRoute><OrdersDetailPage /></ProtectedRoute>} />
                <Route path="/orders/:id/tracking" element={<ProtectedRoute><OrderTrackingPage /></ProtectedRoute>} />
                <Route path="/order-tracking/:id" element={<ProtectedRoute><OrderTrackingPage /></ProtectedRoute>} />
                <Route path="/orders/:id/report-issue" element={<ProtectedRoute><OrderReportIssuePage /></ProtectedRoute>} />
                <Route path="/report-issue/:id" element={<ProtectedRoute><OrderReportIssuePage /></ProtectedRoute>} />
                <Route path="/orders/:id/review" element={<ProtectedRoute><OrderReviewPage /></ProtectedRoute>} />
                <Route path="/create-order/:service" element={<ProtectedRoute><CreateOrder /></ProtectedRoute>} />
                <Route path="/referrals" element={<ProtectedRoute><ReferralsPage /></ProtectedRoute>} />
                <Route path="/shop" element={<ProtectedRoute><Shop /></ProtectedRoute>} />
                <Route path="/pickup-delivery" element={<ProtectedRoute><PickupDelivery /></ProtectedRoute>} />
                <Route path="/cargo-delivery" element={<ProtectedRoute><CargoDelivery /></ProtectedRoute>} />
                <Route path="/payment" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
                <Route path="/payment/:paymentId" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
                <Route path="/handyman-payment/:orderId" element={<ProtectedRoute><HandymanFinalPaymentPage /></ProtectedRoute>} />
              </Routes>
            </WebSocketProvider>
          </LocationProvider>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
